// Relecture avant dépôt : demandeur, réponses lisibles (champs visibles seulement), pièces, délai
// et coût ; dépôt avec erreurs du serveur renvoyées vers l'étape concernée ; confirmation avec numéro.
import { tousLesChamps, visibilites, type DonneesFormulaire, type Formulaire } from '@cc/contrats';
import { Bandeau, Bouton, LienBouton } from '@cc/ui';
import { date } from '../../../commun/format';
import { Bloc, TexteStatut } from '../commun';
import { LIBELLES_VERDICT } from '../pieces';
import type { FicheDemarche, FicheDossierUsager } from '../types';
import { valeurLisible } from './formulaire-depuis-fiche';
import styles from './depot.module.css';
import commun from '../citoyen.module.css';

export type EtapeDepot = 'eligibilite' | 'formulaire' | 'pieces' | 'relecture' | 'confirmation';

export interface ErreurDepot {
  message: string;
  etape: EtapeDepot;
}

/** Erreurs d'un dépôt refusé (400) ramenées à l'étape où les corriger. */
export function erreursDepot(details: { champ: string; message: string }[], code: string | null, message: string | null): ErreurDepot[] {
  const liste: ErreurDepot[] = details.map((d) => ({
    message: d.message,
    etape: d.champ.startsWith('donnees_formulaire.') ? 'formulaire' : d.champ.startsWith('pieces') ? 'pieces' : d.champ === 'eligibilite' ? 'eligibilite' : 'relecture',
  }));
  if (liste.length === 0 && message) liste.push({ message, etape: code === 'identite_non_verifiee' ? 'eligibilite' : 'relecture' });
  return liste;
}

export interface ProprietesEtapeRelecture {
  fiche: FicheDemarche;
  formulaire: Formulaire;
  dossier: FicheDossierUsager;
  donnees: DonneesFormulaire;
  erreurs: ErreurDepot[];
  onAller: (etape: EtapeDepot) => void;
  onDeposer: () => void;
  enCours: boolean;
}

export function EtapeRelecture({ fiche, formulaire, dossier, donnees, erreurs, onAller, onDeposer, enCours }: ProprietesEtapeRelecture) {
  const vis = visibilites(formulaire, donnees);
  const champs = tousLesChamps(formulaire).filter((x) => vis.champs.has(x.champ.code));
  const manquantes = dossier.pieces_attendues.filter((p) => p.obligatoire && !p.fournie);
  const pieces = dossier.pieces.map((p) => ({ id: p.document_id, titre: p.titre }));
  return (
    <div className={commun.pile}>
      {erreurs.length > 0 ? (
        <Bandeau type="erreur" titre="La demande n'a pas été déposée">
          <ul>
            {erreurs.map((e, i) => (
              <li key={`${e.message}-${i}`}>
                {e.message}
                {e.etape !== 'relecture' ? (
                  <>
                    {' '}
                    <button type="button" className={commun.lienSection} onClick={() => onAller(e.etape)}>
                      Corriger
                    </button>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        </Bandeau>
      ) : null}
      <section className={commun.carte} aria-labelledby="recap-demandeur">
        <h2 id="recap-demandeur">Demandeur</h2>
        <p>{dossier.representation ? `Au nom de ${dossier.representation.entite_nom}` : 'En votre nom'} · {fiche.collectivite.nom}</p>
      </section>
      <section className={commun.carte} aria-labelledby="recap-reponses">
        <div className={styles.pieceEntete}>
          <h2 id="recap-reponses">Vos réponses</h2>
          <Bouton variante="discret" petit onClick={() => onAller('formulaire')}>
            Modifier
          </Bouton>
        </div>
        {champs.length === 0 ? (
          <p className={commun.aide}>Cette démarche ne comporte aucun champ à remplir.</p>
        ) : (
          <dl className={styles.recap}>
            {champs.map(({ champ }) => (
              <div key={champ.code}>
                <dt>{champ.libelle}</dt>
                <dd>{valeurLisible(champ, donnees[champ.code], pieces)}</dd>
              </div>
            ))}
          </dl>
        )}
      </section>
      <section className={commun.carte} aria-labelledby="recap-pieces">
        <div className={styles.pieceEntete}>
          <h2 id="recap-pieces">Pièces</h2>
          <Bouton variante="discret" petit onClick={() => onAller('pieces')}>
            Modifier
          </Bouton>
        </div>
        {dossier.pieces.length === 0 ? <p className={commun.aide}>Aucune pièce jointe.</p> : null}
        <ul>
          {dossier.pieces.map((p) => (
            <li key={p.id}>
              {p.titre} <TexteStatut libelle={LIBELLES_VERDICT[p.verdict]?.libelle ?? p.verdict} ton={LIBELLES_VERDICT[p.verdict]?.ton ?? 'neutre'} />
            </li>
          ))}
        </ul>
        {manquantes.length > 0 ? <p className={commun.statutAttention}>Pièces obligatoires manquantes : {manquantes.map((m) => m.libelle).join(', ')}.</p> : null}
      </section>
      <section className={commun.carte} aria-labelledby="recap-conditions">
        <h2 id="recap-conditions">Délai et coût</h2>
        <dl className={styles.recap}>
          <div>
            <dt>Délai de réponse</dt>
            <dd>{fiche.delais.reglementaire?.libelle ?? 'Non fixé'}</dd>
          </div>
          <div>
            <dt>Coût</dt>
            <dd>
              {fiche.tarif.libelle}
              {fiche.tarif.mention ? ' ; aucun paiement n\'est demandé avant le lot L2' : ''}
            </dd>
          </div>
        </dl>
      </section>
      <p className={commun.aide}>En déposant, vous certifiez l&apos;exactitude de vos réponses. Vous pourrez retirer la demande tant qu&apos;aucune décision n&apos;est prise.</p>
      <Bouton pleineLargeur chargement={enCours} libelleChargement="Dépôt en cours" onClick={onDeposer} disabled={manquantes.length > 0}>
        Déposer la demande
      </Bouton>
    </div>
  );
}

export function Confirmation({ dossier }: { dossier: FicheDossierUsager }) {
  return (
    <div className={commun.pile}>
      <Bloc etiquette="Demande déposée" titre={dossier.demarche.libelle}>
        <span className={styles.numero} aria-label={`Numéro ${dossier.numero ?? ''}`}>
          {dossier.numero}
        </span>
        <p className={commun.blocTexte}>
          Déposée le {date(dossier.enregistre_le ?? dossier.cree_le)} auprès de {dossier.collectivite.nom}. Statut : <TexteStatut code={dossier.statut.code} />.
          {dossier.echeance_reglementaire ? ` Réponse attendue au plus tard le ${date(dossier.echeance_reglementaire)}.` : ''}
        </p>
        <p className={commun.blocTexte}>Conservez ce numéro. Vous serez prévenu à chaque changement, ici et par le canal de la démarche.</p>
      </Bloc>
      <div className={commun.ligneBoutons}>
        <LienBouton href={`/citoyen/mes-dossiers/${dossier.id}`} variante="principal">
          Suivre ma demande
        </LienBouton>
        <LienBouton href={`/citoyen/${dossier.collectivite.code}`} variante="secondaire">
          Retour à l&apos;accueil
        </LienBouton>
      </div>
    </div>
  );
}
